import React from 'react';
import { FiArrowLeft, FiCheckCircle, FiMapPin } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { LocationContactLocation, submitLocationContactRequest } from '../../services/contactApi';

type IconComponent = React.ComponentType<{ className?: string }>;
const ArrowLeftIcon = FiArrowLeft as IconComponent;
const CheckCircleIcon = FiCheckCircle as IconComponent;
const MapPinIcon = FiMapPin as IconComponent;

type FormData = {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  message: string;
};

interface ChatLocationContactPanelProps {
  location: LocationContactLocation;
  onBack: () => void;
}

const ChatLocationContactPanel: React.FC<ChatLocationContactPanelProps> = ({ location, onBack }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = React.useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState('');
  const [isSuccess, setIsSuccess] = React.useState(false);

  const locationLabel = t(`chatbot.locationContact.locations.${location}`);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'email' ? value.trimStart() : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError('');

    const cleanedFullName = formData.fullName.replace(/\s+/g, ' ').trim();
    const cleanedEmail = formData.email.trim().toLowerCase();
    const cleanedPhone = formData.phone.replace(/\s+/g, ' ').trim();
    const cleanedCompany = formData.company.replace(/\s+/g, ' ').trim();
    const cleanedMessage = formData.message.replace(/\s+/g, ' ').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneDigits = cleanedPhone.replace(/[^\d]/g, '');

    if (cleanedFullName.length < 2 || cleanedFullName.length > 80) {
      setFormError(t('chatbot.locationContact.validation.fullName'));
      return;
    }
    if (!emailRegex.test(cleanedEmail)) {
      setFormError(t('chatbot.locationContact.validation.email'));
      return;
    }
    if (phoneDigits.length < 6 || phoneDigits.length > 15) {
      setFormError(t('chatbot.locationContact.validation.phone'));
      return;
    }
    if (cleanedMessage.length < 10 || cleanedMessage.length > 1000) {
      setFormError(t('chatbot.locationContact.validation.message'));
      return;
    }

    setIsSubmitting(true);
    try {
      await submitLocationContactRequest({
        location,
        fullName: cleanedFullName,
        email: cleanedEmail,
        phone: cleanedPhone,
        company: cleanedCompany || undefined,
        message: cleanedMessage,
      });
      setIsSuccess(true);
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : t('chatbot.locationContact.validation.submitFailed')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-slate-50 px-6 py-8 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal-100 text-teal-600">
          <CheckCircleIcon className="h-7 w-7" />
        </div>
        <h4 className="text-base font-semibold text-slate-800">{t('chatbot.locationContact.successTitle')}</h4>
        <p className="mt-2 max-w-xs text-sm leading-relaxed text-slate-600">
          {t('chatbot.locationContact.success')}
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-6 rounded-xl bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-teal-700"
        >
          {t('chatbot.locationContact.backToChat')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-slate-50">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
          aria-label={t('chatbot.locationContact.backAria')}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">
            {t('chatbot.locationContact.formTitle', { location: locationLabel })}
          </p>
          <p className="truncate text-xs text-slate-500">{t('chatbot.locationContact.formSubtitle')}</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-teal-700">
          <MapPinIcon className="h-3 w-3" />
          {locationLabel}
        </span>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div
          data-lenis-prevent
          onWheel={(e) => e.stopPropagation()}
          className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 [scrollbar-width:thin] [scrollbar-color:#14b8a6_#dbeafe] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-slate-200/70 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-teal-500"
        >
          <div>
            <label htmlFor="chat-fullName" className="mb-1.5 block text-xs font-semibold text-slate-700">
              {t('chatbot.locationContact.fields.fullName')}
            </label>
            <input
              id="chat-fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder={t('chatbot.locationContact.placeholders.fullName')}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              required
              minLength={2}
              maxLength={80}
              autoComplete="name"
            />
          </div>

          <div>
            <label htmlFor="chat-email" className="mb-1.5 block text-xs font-semibold text-slate-700">
              {t('chatbot.locationContact.fields.email')}
            </label>
            <input
              id="chat-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('chatbot.locationContact.placeholders.email')}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              required
              maxLength={254}
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="chat-phone" className="mb-1.5 block text-xs font-semibold text-slate-700">
              {t('chatbot.locationContact.fields.phone')}
            </label>
            <input
              id="chat-phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('chatbot.locationContact.placeholders.phone')}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              required
              maxLength={20}
              autoComplete="tel"
            />
          </div>

          <div>
            <label htmlFor="chat-company" className="mb-1.5 block text-xs font-semibold text-slate-700">
              {t('chatbot.locationContact.fields.company')}
            </label>
            <input
              id="chat-company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder={t('chatbot.locationContact.placeholders.company')}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              maxLength={120}
              autoComplete="organization"
            />
          </div>

          <div>
            <label htmlFor="chat-message" className="mb-1.5 block text-xs font-semibold text-slate-700">
              {t('chatbot.locationContact.fields.message')}
            </label>
            <textarea
              id="chat-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              placeholder={t('chatbot.locationContact.placeholders.message')}
              className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              required
              minLength={10}
              maxLength={1000}
            />
          </div>

          {formError && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">{formError}</p>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white p-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-xl bg-teal-600 py-3 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {isSubmitting
              ? t('chatbot.locationContact.submitting')
              : t('chatbot.locationContact.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatLocationContactPanel;